/** `VisualiserTextBox`'s own default when a TEXT element omits `fontSize`. */
const DEFAULT_FONT_PX = 21

/**
 * Reads a config length as CSS pixels.
 *
 * Config values arrive as `"78px"`, `"78"`, `78` or `"100%"`. Percentages resolve against the face
 * dimension they are written against; anything else (`auto`, `calc(...)`, empty) yields null so the
 * caller can fall back.
 *
 * @param value - The raw config value.
 * @param base - The dimension a percentage is relative to.
 * @returns The length in CSS pixels, or null when it cannot be resolved.
 */
export function parseCssLength(
  value: string | number | null | undefined,
  base: number,
): number | null {
  if (typeof value === "number") return Number.isFinite(value) ? value : null
  if (typeof value !== "string") return null
  const trimmed = value.trim()
  if (!trimmed) return null
  const percent = /^(-?\d*\.?\d+)\s*%$/.exec(trimmed)
  if (percent) {
    const ratio = Number.parseFloat(percent[1])
    return Number.isFinite(ratio) ? (base * ratio) / 100 : null
  }
  const pixels = /^(-?\d*\.?\d+)\s*(px)?$/.exec(trimmed)
  if (!pixels) return null
  const parsed = Number.parseFloat(pixels[1])
  return Number.isFinite(parsed) ? parsed : null
}

/**
 * Reads a config `fontSize` as CSS pixels.
 *
 * Bare numbers are pixels (Liquid writes them unsuffixed); `rem` is resolved against the 16 px root
 * the theme uses. An unresolvable value falls back to `VisualiserTextBox`'s own default.
 *
 * @param fontSize - The raw config value.
 * @returns A positive pixel size.
 */
export function resolveFontSizePx(fontSize: string | number | null | undefined): number {
  if (typeof fontSize === "number" && Number.isFinite(fontSize) && fontSize > 0) return fontSize
  if (typeof fontSize === "string") {
    const rem = /^\s*(-?\d*\.?\d+)\s*rem\s*$/.exec(fontSize)
    if (rem) {
      const parsed = Number.parseFloat(rem[1]) * 16
      if (Number.isFinite(parsed) && parsed > 0) return parsed
    }
    const px = parseCssLength(fontSize, 0)
    if (px != null && px > 0) return px
  }
  return DEFAULT_FONT_PX
}
